const MINOR_WORDS = new Set(["a", "i", "o", "u", "w", "z"]);

const SEPARATORS = /([\s\-/]+)/;

const LOWERCASE_LETTER = /\p{Ll}/u;
const UPPERCASE_LETTER = /\p{Lu}/u;
const FIRST_LETTER = /\p{L}/u;
const NON_LETTER = /[^\p{L}]/gu;
const WHITESPACE = /\s+/;

/**
 * An all-caps word this short reads as an acronym rather than a capslock slip
 * (IG, XL, DR), so it is left alone.
 */
const ACRONYM_MAX_LENGTH = 2;

const countLetters = (value: string) => value.replace(NON_LETTER, "").length;

const countWords = (name: string) => name.split(WHITESPACE).length;

const isAllCaps = (value: string) =>
  UPPERCASE_LETTER.test(value) && !LOWERCASE_LETTER.test(value);

const capitalize = (word: string) => {
  const firstLetterIndex = word.search(FIRST_LETTER);
  if (firstLetterIndex === -1) return word;

  return (
    word.slice(0, firstLetterIndex) +
    word.charAt(firstLetterIndex).toUpperCase() +
    word.slice(firstLetterIndex + 1).toLowerCase()
  );
};

const formatWord = (word: string, isFirst: boolean) => {
  if (LOWERCASE_LETTER.test(word)) return word;

  if (!isFirst && MINOR_WORDS.has(word.toLowerCase()))
    return word.toLowerCase();

  if (!isAllCaps(word)) return word;
  if (countLetters(word) <= ACRONYM_MAX_LENGTH) return word;

  return capitalize(word);
};

const formatWords = (name: string) =>
  name
    .split(SEPARATORS)
    .map((part, index) => {
      const isSeparator = index % 2 === 1;
      if (isSeparator) return part;

      return formatWord(part, index === 0);
    })
    .join("");

/**
 * Normalizes capslocked words in a catering name for display.
 *
 * Catering names are brands, so this stays deliberately conservative: a
 * single-word name is never touched, because an all-caps single word is far
 * more likely to be branding (SPOKOBOX) than a capslock slip, and we have no
 * way of knowing where the brand wanted its capitals.
 *
 * @example
 * formatCateringName("DRWAL W KUCHNI") // "Drwal w Kuchni"
 * formatCateringName("SpokoBOX") // "SpokoBOX"
 * formatCateringName("SPOKOBOX") // "SPOKOBOX" — brand left alone
 */
export const formatCateringName = (name: string): string => {
  const trimmed = name.trim();

  if (countWords(trimmed) < 2) return trimmed;

  return formatWords(trimmed);
};

/**
 * Normalizes capslocked words in a diet name for display.
 *
 * Diet names describe the diet ("Odchudzający", "WYBÓR MENU Smart") rather than
 * carry the brand — the brand lives in the catering name — so single-word names
 * are reformatted too. Words are judged one by one, so the deliberate parts of
 * a mixed name survive untouched.
 *
 * @example
 * formatDietName("ODCHUDZAJĄCY") // "Odchudzający"
 * formatDietName("WYBÓR MENU Smart") // "Wybór Menu Smart"
 * formatDietName("SPORT VEGAN gf") // "Sport Vegan gf"
 * formatDietName("IG") // "IG"
 */
export const formatDietName = (name: string): string =>
  formatWords(name.trim());
